import { ElementIconType } from "@/components/ui/icons";
import {
  IMapPlaceholderVariants,
  MapWaypointsRendererRef,
  useSvg,
} from "@faro-lotv/app-component-toolbox";
import { PlaceholdersTexture } from "@faro-lotv/lotv";
import { RevisionScanEntity } from "@faro-lotv/service-wires";
import { useEffect, useMemo } from "react";
import ScanMarkerDroneWarningSvg from "../icons/registration-marker-drone-warning-outline.svg";
import ScanMarkerDroneSvg from "../icons/registration-marker-drone.svg";
import ScanMarkerFlashWarningSvg from "../icons/registration-marker-flash-warning-outline.svg";
import ScanMarkerFlashSvg from "../icons/registration-marker-flash.svg";
import ScanMarkerUnusableSvg from "../icons/registration-marker-unusable.svg";
import ScanMarkerWarningSvg from "../icons/registration-marker-warning-outline.svg";
import { RegistrationStatusMap } from "../store/registration-status-selectors";
import { iconForCaptureTreeElement } from "../ui/scan-tree/entity-icon";
import {
  doesEntityRequireReview,
  HOVER_FACTOR,
  isScanEntityUsable,
} from "../utils/entity-utils";
import {
  PLACEHOLDER_SIZE,
  PLACEHOLDER_WARNING_SIZE,
} from "./canvas-scan-markers";

/** Special variants of the scan markers. */
export enum MarkerLabel {
  /** Flash scans, e.g. Orbis Flash. */
  flash = "flash",
  /** A warning icon for flash scans, because the registration requires manual review. */
  flashWarning = "flashWarning",
  /** Drone captures (via photogrammetry). */
  drone = "drone",
  /** Drone captures that require manual review. */
  droneWarning = "droneWarning",
  /** A warning icon, because the registration requires manual review. */
  warning = "warning",
  /** An unusable scan that cannot be displayed, e.g. due to failed processing. */
  unusable = "unusable",
}

export type LabelAssignment = Record<MarkerLabel, number[]>;

/**
 * @param scanEntities The scan entities to determine the label assignment for
 * @param statusMap A map from entity IDs to their registration statuses
 * @returns The label assignment for each scan entity, determining which icon to use
 */
export function determineLabelAssignment(
  scanEntities: RevisionScanEntity[],
  statusMap: RegistrationStatusMap,
): LabelAssignment {
  const warningIdx: number[] = [];
  const unusableIdx: number[] = [];
  const flashIdx: number[] = [];
  const flashWarningIdx: number[] = [];
  const droneIdx: number[] = [];
  const droneWarningIdx: number[] = [];

  let assignment: number[] | undefined;

  for (const [scanEntityIdx, scanEntity] of scanEntities.entries()) {
    const requiresReview = doesEntityRequireReview(scanEntity, statusMap);

    if (!isScanEntityUsable(scanEntity)) {
      // Unusable marker
      assignment = unusableIdx;
    } else if (
      iconForCaptureTreeElement(scanEntity) === ElementIconType.ScanFlashIcon
    ) {
      // Flash scan marker
      assignment = requiresReview ? flashWarningIdx : flashIdx;
    } else if (
      iconForCaptureTreeElement(scanEntity) === ElementIconType.DroneScanIcon
    ) {
      // Drone scan marker
      assignment = requiresReview ? droneWarningIdx : droneIdx;
    } else if (requiresReview) {
      // Warning marker
      assignment = warningIdx;
    } else {
      // Default marker
      assignment = undefined;
    }

    if (assignment) {
      assignment.push(scanEntityIdx);
    }
  }

  return {
    [MarkerLabel.flash]: flashIdx,
    [MarkerLabel.flashWarning]: flashWarningIdx,
    [MarkerLabel.drone]: droneIdx,
    [MarkerLabel.droneWarning]: droneWarningIdx,
    [MarkerLabel.warning]: warningIdx,
    [MarkerLabel.unusable]: unusableIdx,
  };
}

/**
 * @param waypointRenderer The renderer to apply the label assignment to
 * @param labelAssignment The assignment of labels to the indexes of the scan entities
 */
export function useApplyLabelAssignment(
  waypointRenderer: MapWaypointsRendererRef | null,
  labelAssignment: LabelAssignment,
): void {
  useEffect(() => {
    if (!waypointRenderer) return;

    // Assign the labels to the scan markers
    for (const [label, indices] of Object.entries(labelAssignment)) {
      waypointRenderer.setLabeledPlacehoders(label, indices);
    }
  }, [waypointRenderer, labelAssignment]);
}

const replaceWarningColor = {
  // Replace the warning color inside the SVG (must match exactly) with pure green
  // This allows the different color parts to be distinguished in the shader,
  // so that they can be customized per instance
  //
  // IMPORTANT: Keep in sync with the SVGs:
  // - scan-marker-warning-outline.svg
  // - scan-marker-flash-warning-outline.svg
  // - scan-marker-drone-warning-outline.svg
  ["#E8A600"]: "#00ff00",
};

/**
 * @param waypointRenderer The renderer to apply the label textures to
 */
export function useScanMarkerLabelTextures(
  waypointRenderer: MapWaypointsRendererRef | null,
): void {
  const flashTextures = useTextureVariants(ScanMarkerFlashSvg);
  const droneTextures = useTextureVariants(ScanMarkerDroneSvg);
  const unusableTextures = useTextureVariants(ScanMarkerUnusableSvg);
  // colorsToReplace to allow the GPU shader to reliably detect the non grey scale colors
  const warningTextures = useTextureVariants(
    ScanMarkerWarningSvg,
    replaceWarningColor,
    PLACEHOLDER_WARNING_SIZE,
  );
  const warningFlashTextures = useTextureVariants(
    ScanMarkerFlashWarningSvg,
    replaceWarningColor,
    PLACEHOLDER_WARNING_SIZE,
  );
  const warningDroneTextures = useTextureVariants(
    ScanMarkerDroneWarningSvg,
    replaceWarningColor,
    PLACEHOLDER_WARNING_SIZE,
  );

  useEffect(() => {
    if (!waypointRenderer) return;

    // Warning markers have a thicker border
    // Increase the total size to keep the inner circle size consistent
    const warningSize = 24;

    configureLabeledTextures(
      waypointRenderer,
      MarkerLabel.flash,
      flashTextures,
    );
    configureLabeledTextures(
      waypointRenderer,
      MarkerLabel.drone,
      droneTextures,
    );
    configureLabeledTextures(
      waypointRenderer,
      MarkerLabel.warning,
      warningTextures,
      warningSize,
    );
    configureLabeledTextures(
      waypointRenderer,
      MarkerLabel.flashWarning,
      warningFlashTextures,
      warningSize,
    );
    configureLabeledTextures(
      waypointRenderer,
      MarkerLabel.droneWarning,
      warningDroneTextures,
      warningSize,
    );
    configureLabeledTextures(
      waypointRenderer,
      MarkerLabel.unusable,
      unusableTextures,
    );
  }, [
    waypointRenderer,
    flashTextures,
    droneTextures,
    warningTextures,
    warningFlashTextures,
    warningDroneTextures,
    unusableTextures,
  ]);
}

/**
 * @param svg the SVG to create the texture variants from
 * @param colorsToReplace the colors/vars in the SVG to replace with different colors
 * @param sizeOverride a custom size that the SVG should be rendered at
 * @returns The same texture for all states (default, hovered, selected).
 */
export function useTextureVariants(
  svg: string,
  colorsToReplace?: Record<string, string>,
  sizeOverride?: number,
): IMapPlaceholderVariants {
  const baseSize = sizeOverride ?? PLACEHOLDER_SIZE;
  const svgOptions = useMemo(
    () => ({
      // The pure red can be detected in the shader and replaced with the instance color
      // This allows variable colors for different scan marker instances
      currentColor: "#ff0000",
      colorsToReplace,
      // Render image at higher resolution to reduce rendering artifacts
      width: baseSize * HOVER_FACTOR * 2,
      height: baseSize * HOVER_FACTOR * 2,
      devicePixelRatio: window.devicePixelRatio,
    }),
    [colorsToReplace, baseSize],
  );
  const texture = useSvg(svg, svgOptions);

  return useMemo(
    () => ({
      defaultTexture: texture,
      selectedTexture: texture,
      hoveredTexture: texture,
    }),
    [texture],
  );
}

/**
 * @param waypointRenderer The renderer to configure the texture labels for.
 * @param label The name of the label to configure.
 * @param textures The texture to use for the label.
 * @param sizeOverride A custom size to use, instead of the default.
 */
function configureLabeledTextures(
  waypointRenderer: MapWaypointsRendererRef | null,
  label: string,
  textures: IMapPlaceholderVariants,
  sizeOverride?: number,
): void {
  if (!waypointRenderer) return;

  // Use the same texture for all states
  waypointRenderer.setLabeledMap(
    label,
    PlaceholdersTexture.Default,
    textures.defaultTexture,
  );
  waypointRenderer.setLabeledMap(
    label,
    PlaceholdersTexture.Hovered,
    textures.hoveredTexture,
  );
  waypointRenderer.setLabeledMap(
    label,
    PlaceholdersTexture.Selected,
    textures.selectedTexture,
  );

  const size = sizeOverride ?? PLACEHOLDER_SIZE;

  waypointRenderer.setLabelSizes(label, {
    default: size,
    hovered: size * HOVER_FACTOR,
    selected: size * HOVER_FACTOR,
  });
}
